import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const IdeaVisual = () => (
  <svg viewBox="0 0 420 320" className="h-auto w-full" data-testid="idea-visual">
    <defs>
      <linearGradient id="goldLine" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#8a6a1f" />
        <stop offset="50%" stopColor="#FFD700" />
        <stop offset="100%" stopColor="#D4AF37" />
      </linearGradient>
    </defs>
    {[
      "M20 260 C 120 120, 200 300, 400 60",
      "M10 180 C 140 260, 260 40, 410 200",
      "M40 40 C 160 200, 300 120, 380 280",
      "M20 300 C 180 240, 240 100, 400 140",
    ].map((d, i) => (
      <motion.path
        key={d}
        d={d}
        fill="none"
        stroke="url(#goldLine)"
        strokeWidth="1.4"
        strokeLinecap="round"
        initial={{ pathLength: 0, opacity: 0 }}
        whileInView={{ pathLength: 1, opacity: 0.95 - i * 0.18 }}
        viewport={{ margin: "-20%" }}
        transition={{ duration: 2.4, delay: i * 0.3, ease: "easeInOut" }}
      />
    ))}
  </svg>
);

const wireRows = [
  { h: "h-16", w: "w-full", glow: true },
  { h: "h-2", w: "w-3/4" },
  { h: "h-2", w: "w-1/2" },
  { h: "h-20", w: "w-full", split: true },
  { h: "h-2", w: "w-2/3" },
];

const ConceptVisual = () => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ margin: "-20%" }}
    className="rounded-xl border border-[#D4AF37]/25 bg-[#0d0d0d]/80 p-5 backdrop-blur-md"
    data-testid="concept-visual"
  >
    <div className="mb-5 flex gap-1.5">
      {[0, 1, 2].map((i) => (
        <span key={i} className="h-2 w-2 rounded-full bg-[#D4AF37]/40" />
      ))}
    </div>
    <div className="space-y-4">
      {wireRows.map((r, i) =>
        r.split ? (
          <div key={i} className="flex gap-4">
            <motion.div
              custom={i}
              variants={{
                hidden: { scaleX: 0 },
                visible: { scaleX: 1, transition: { delay: i * 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
              }}
              className="h-20 w-1/2 origin-left rounded border border-[#D4AF37]/20 bg-gradient-to-br from-[#1A1510] to-transparent"
            />
            <motion.div
              custom={i + 0.5}
              variants={{
                hidden: { scaleX: 0 },
                visible: { scaleX: 1, transition: { delay: (i + 0.5) * 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
              }}
              className="h-20 w-1/2 origin-left rounded border border-white/10 bg-white/[0.03]"
            />
          </div>
        ) : (
          <motion.div
            key={i}
            custom={i}
            variants={{
              hidden: { scaleX: 0 },
              visible: { scaleX: 1, transition: { delay: i * 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] } },
            }}
            className={`${r.h} ${r.w} origin-left rounded ${
              r.glow
                ? "border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/20 via-[#1A1510] to-transparent"
                : "bg-white/10"
            }`}
          />
        )
      )}
    </div>
  </motion.div>
);

const chips = [
  { label: "Bild", x: "6%", y: "8%", d: 1.1 },
  { label: "Headline", x: "52%", y: "0%", d: 0.7 },
  { label: "Button", x: "70%", y: "38%", d: 1.3 },
  { label: "Animation", x: "12%", y: "55%", d: 0.8 },
  { label: "UI-Element", x: "42%", y: "68%", d: 1.2 },
  { label: "Farbe", x: "74%", y: "74%", d: 0.9 },
];

function Chip({ chip, progress }) {
  const y = useTransform(progress, [0, 1], [90 * chip.d, -90 * chip.d]);
  return (
    <motion.span
      style={{ left: chip.x, top: chip.y, y }}
      className="absolute rounded-full border border-[#D4AF37]/30 bg-[#121212]/70 px-4 py-2 text-[11px] tracking-[0.2em] text-[#FFE9A8] backdrop-blur-md"
    >
      {chip.label}
    </motion.span>
  );
}

const WowVisual = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  return (
    <div ref={ref} className="relative h-72 md:h-80" data-testid="wow-visual">
      <div className="absolute left-1/2 top-1/2 h-40 w-40 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#D4AF37]/10 blur-3xl" />
      {chips.map((c) => (
        <Chip key={c.label} chip={c} progress={scrollYProgress} />
      ))}
    </div>
  );
};

const chapters = [
  {
    id: "01",
    title: "Die Idee",
    claim: "Aus einer Idee entsteht eine Vision.",
    copy: "Jede großartige Website beginnt mit einem Gespräch. Wir hören zu, stellen Fragen zu Ihrem Betrieb, Ihren Kunden und Ihrem Markt — und finden den Kern Ihrer Marke, lange bevor die erste Zeile Design entsteht.",
    visual: <IdeaVisual />,
  },
  {
    id: "02",
    title: "Das Konzept",
    claim: "Struktur wird Strategie.",
    copy: "Aus Linien werden Layouts. Wir verwandeln Ihre Vision in ein durchdachtes Konzept für Ihre neue Website: Seitenstruktur, Nutzerführung, Inhalte und Suchbegriffe — jeder Schritt hat einen Grund.",
    visual: <ConceptVisual />,
  },
  {
    id: "03",
    title: "Der WOW-Moment",
    claim: "Design, das man spürt.",
    copy: "Hier entsteht die Magie: Typografie, Bewegung, Licht und Raum verschmelzen zu einem Auftritt, den Ihre Besucher nicht mehr vergessen — auf dem Smartphone genauso wie am Bildschirm.",
    visual: <WowVisual />,
  },
];

function WordBelt() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const x = useTransform(scrollYProgress, [0, 1], ["10%", "-10%"]);
  const words = ["IDEE", "KONZEPT", "DESIGN", "ERLEBNIS"];
  return (
    <div ref={ref} className="overflow-hidden py-16 md:py-24" data-testid="word-belt">
      <motion.div style={{ x }} className="flex items-center justify-center gap-6 whitespace-nowrap md:gap-10">
        {words.map((w, i) => (
          <span key={w} className="flex items-center gap-6 md:gap-10">
            <span
              className={`font-display text-4xl font-extrabold uppercase tracking-tight md:text-6xl ${
                i === words.length - 1 ? "gold-text" : "text-stroke-gold opacity-70"
              }`}
            >
              {w}
            </span>
            {i < words.length - 1 && <span className="h-px w-8 bg-[#D4AF37]/40 md:w-14" />}
          </span>
        ))}
      </motion.div>
    </div>
  );
}

export default function Manifesto() {
  return (
    <section id="ablauf" data-testid="manifesto-section" className="relative z-10 px-6 py-28 md:px-14 md:py-40">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="mb-6 text-[11px] tracking-[0.45em] text-[#D4AF37] md:text-sm">DER ABLAUF · WEBSITE ERSTELLEN LASSEN</p>
          <h2 className="max-w-3xl font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
            Vom ersten Gespräch zur fertigen{" "}
            <em className="gold-text font-serif-accent normal-case italic">Website.</em>
          </h2>
        </motion.div>

        {chapters.map((c, i) => (
          <div
            key={c.id}
            data-testid={`chapter-${c.id}`}
            className="mt-24 grid items-center gap-12 border-t border-white/5 pt-16 md:mt-32 md:grid-cols-2 md:pt-24"
          >
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15%" }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className={i % 2 === 1 ? "md:order-2" : ""}
            >
              <span className="text-stroke-gold font-display text-7xl font-extrabold md:text-8xl">
                {c.id}
              </span>
              <h3 className="mt-4 font-display text-2xl font-bold uppercase tracking-tight md:text-3xl">
                {c.title}
              </h3>
              <p className="gold-text mt-2 font-serif-accent text-xl italic md:text-2xl">{c.claim}</p>
              <p className="mt-6 max-w-md text-base font-light leading-relaxed text-[#BDBDB6] md:text-lg">
                {c.copy}
              </p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-15%" }}
              transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
              className={i % 2 === 1 ? "md:order-1" : ""}
            >
              {c.visual}
            </motion.div>
          </div>
        ))}
      </div>
      <WordBelt />
    </section>
  );
}
